import type RootStore from "stores/RootStore";
import { RouteType } from "types/RouteType";
import type ApiKeysStore from "./ApiKeysStore";

/**
 * Admin → API Keys.
 *
 * The create / reveal / confirm dialogs are REGISTERED HERE rather than rendered
 * inline by the body. That is the house pattern (see
 * views/administration/UserManagement/userManagement.Route.ts) and it is what
 * gives them the router's standard dialog chrome and its
 * `forceFullScreenDialog` on phones — the hand-rolled MUI `<Dialog>`s they
 * replaced had neither, which is most of why this screen read as broken on a
 * small viewport.
 */

const apiKeysStoreOf = (rootStore: RootStore) =>
  rootStore.apiKeysViewStore as unknown as ApiKeysStore;

/**
 * A failed write THROWS: the dialog framework keeps a dialog open when its
 * onSubmit rejects and shows the message in the footer, so the API error stays
 * visible with the form state preserved for correction. Resolving closes it.
 *
 * `onSuccess` is deferred a tick: the framework's own close runs after onSubmit
 * resolves, so a dialog opened synchronously here would be closed again
 * immediately.
 */
const submitOrThrow = async (
  rootStore: RootStore,
  submit: (store: ApiKeysStore) => Promise<boolean>,
  errorOf: (store: ApiKeysStore) => string | null,
  fallback: string,
  onSuccess?: (store: ApiKeysStore) => void
) => {
  const store = apiKeysStoreOf(rootStore);
  if (!(await submit(store))) throw new Error(errorOf(store) || fallback);
  if (onSuccess) setTimeout(() => onSuccess(store), 0);
};

const apiKeysRoute: RouteType = {
  // Name must normalize (spaces stripped) to the same key as the title —
  // the left menu and omnibar navigate via goTo(title) → key "APIKeys".
  name: "API Keys",
  title: "API Keys",
  path: "/admin/api-keys",
  layout: "/admin",
  viewportCard: {
    header: {
      enableBackButton: true,
      title: "API Keys",
      // false: this page loads from its Body, not an onEnter, so the plate's
      // refresh (which re-runs onEnter) would do nothing. The header renders
      // the working Refresh button.
      enableRefreshButton: false,
      color: "primary",
      component: () => import("./ApiKeys.Header"),
    },
    body: {
      component: () => import("./ApiKeys.Body"),
      grayBackground: false,
    },
  },
  group: "Admin",
  searchAliases: ["tokens", "machine keys", "service accounts"],
  requiresAuth: true,
  requiresAdmin: true,
  requiresPermission: false,
  enableAutoFind: false,
  dialogs: [
    {
      name: "CreateApiKeyDialog",
      title: "Create API Key",
      component: () => import("./dialogs/CreateApiKeyDialog.Body"),
      cancelButton: true,
      maxWidth: "sm",
      // Grew with the scopes editor (one enforced scope with its blast radius
      // stated, plus the labels field).
      height: 640,
      submitButtonText: "Create Key",
      // On success, chain straight into the one-time reveal: the plaintext key
      // is returned by that one response and never again, so there must be no
      // moment where the dialog has closed and the key is not on screen.
      onSubmit: async ({ rootStore }: { rootStore: RootStore }) =>
        submitOrThrow(
          rootStore,
          (store) => store.submitCreate(),
          (store) => store.createError,
          "Failed to create API key",
          (store) => store.openCreatedKeyDialog()
        ),
      onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) =>
        apiKeysStoreOf(rootStore).canSubmitCreate,
    },
    {
      // One-time plaintext reveal. No cancel button: there is no version of
      // this dialog where dismissing it is different from acknowledging it, and
      // a "Cancel" would imply the key could be got back.
      name: "RevealApiKeyDialog",
      title: "API key created",
      component: () => import("./dialogs/RevealApiKeyDialog.Body"),
      cancelButton: false,
      maxWidth: "sm",
      height: 380,
      submitButtonText: "I've stored the key",
      onSubmit: ({ rootStore }: { rootStore: RootStore }) =>
        apiKeysStoreOf(rootStore).dismissCreatedKey(),
      // Closing by any other route (X, backdrop) drops the plaintext key from
      // memory too — it must not sit in the store waiting to be re-rendered.
      onClose: ({ rootStore }: { rootStore: RootStore }) =>
        apiKeysStoreOf(rootStore).dismissCreatedKey(),
    },
    {
      // Revoke and delete share one body but are registered twice: the
      // framework's title and submit label are static strings on the
      // registration and cannot be re-labelled at runtime.
      name: "RevokeApiKeyDialog",
      title: "Revoke API key",
      component: () => import("./dialogs/ConfirmApiKeyActionDialog.Body"),
      cancelButton: true,
      maxWidth: "xs",
      height: 340,
      submitButtonText: "Revoke Key",
      onSubmit: async ({ rootStore }: { rootStore: RootStore }) =>
        submitOrThrow(
          rootStore,
          (store) => store.executeConfirmedAction(),
          (store) => store.actionError,
          "The key could not be revoked."
        ),
      onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) => {
        const store = apiKeysStoreOf(rootStore);
        return store.confirmAction?.type === "revoke" && !store.actioning;
      },
      onClose: ({ rootStore }: { rootStore: RootStore }) =>
        apiKeysStoreOf(rootStore).cancelConfirm(),
    },
    {
      // Hard delete. The framework renders every submit button in primary blue
      // with no destructive variant, so the deliberate second action is an
      // acknowledgement checkbox in the body gating isDeleteConfirmed.
      name: "DeleteApiKeyDialog",
      title: "Delete API key",
      component: () => import("./dialogs/ConfirmApiKeyActionDialog.Body"),
      cancelButton: true,
      maxWidth: "xs",
      height: 440,
      submitButtonText: "Delete Key",
      onSubmit: async ({ rootStore }: { rootStore: RootStore }) =>
        submitOrThrow(
          rootStore,
          (store) => store.executeConfirmedAction(),
          (store) => store.actionError,
          "The key could not be deleted."
        ),
      onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) => {
        const store = apiKeysStoreOf(rootStore);
        return (
          store.confirmAction?.type === "delete" &&
          store.isDeleteConfirmed &&
          !store.actioning
        );
      },
      onClose: ({ rootStore }: { rootStore: RootStore }) =>
        apiKeysStoreOf(rootStore).cancelConfirm(),
    },
    {
      // Grant or remove scopes on a live key without reissuing it. Exists so
      // the Gantry producer key can be given `meeting-analysis:produce` from a
      // phone; the alternative is `yarn api key:grant-scope` on the API host.
      name: "EditApiKeyScopesDialog",
      title: "Edit scopes",
      component: () => import("./dialogs/EditApiKeyScopesDialog.Body"),
      cancelButton: true,
      maxWidth: "sm",
      height: 520,
      submitButtonText: "Save Scopes",
      onSubmit: async ({ rootStore }: { rootStore: RootStore }) =>
        submitOrThrow(
          rootStore,
          (store) => store.submitScopes(),
          (store) => store.scopesError,
          "Failed to save scopes"
        ),
      onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) =>
        apiKeysStoreOf(rootStore).canSubmitScopes,
      onClose: ({ rootStore }: { rootStore: RootStore }) =>
        apiKeysStoreOf(rootStore).cancelScopes(),
    },
  ],
};

export default apiKeysRoute;
